import type { SeededRandom } from "../core/SeededRandom";
import type { GridMap } from "./GridMap";
import { TileType, pointKey, type Point, type Room } from "./types";

export interface RoomEntrance {
  readonly cell: Point;
  readonly orientation: "x" | "y";
  readonly outside: Point;
}

export interface GeneratedCorridor {
  readonly cells: readonly Point[];
  readonly width: number;
  readonly entrance: RoomEntrance;
}

export class CorridorGenerator {
  public constructor(
    private readonly grid: GridMap,
    private readonly random: SeededRandom,
    private readonly rooms: readonly Room[],
  ) {}

  public connect(start: Room, end: Room): GeneratedCorridor {
    const first = this.entrance(start, end.center);
    const last = this.entrance(end, start.center);
    const path = this.route(first.outside, last.outside);
    const cells = [
      ...this.straight(start.center, first.outside),
      ...path,
      ...this.straight(last.outside, end.center),
    ];
    for (const point of cells) {
      for (const cell of this.footprint(point)) this.grid.set(cell, TileType.Floor);
    }
    return { cells, width: 2, entrance: last };
  }

  private entrance(room: Room, target: Point): RoomEntrance {
    const dx = target.x - room.center.x;
    const dy = target.y - room.center.y;
    if (Math.abs(dx) > Math.abs(dy)) {
      const x = dx > 0 ? room.x + room.width : room.x - 1;
      return {
        cell: { x, y: room.center.y },
        orientation: "y",
        outside: { x: x + (dx > 0 ? 1 : -2), y: room.center.y },
      };
    }
    const y = dy > 0 ? room.y + room.height : room.y - 1;
    return {
      cell: { x: room.center.x, y },
      orientation: "x",
      outside: { x: room.center.x, y: y + (dy > 0 ? 1 : -2) },
    };
  }

  private route(start: Point, end: Point): Point[] {
    const queue = [start];
    const previous = new Map<string, Point | undefined>([[pointKey(start), undefined]]);
    const directions = this.random.shuffle([
      { x: 1, y: 0 },
      { x: -1, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: -1 },
    ]);
    for (let index = 0; index < queue.length; index += 1) {
      const current = queue[index] as Point;
      if (pointKey(current) === pointKey(end)) {
        const path: Point[] = [];
        let cursor: Point | undefined = current;
        while (cursor) {
          path.push(cursor);
          cursor = previous.get(pointKey(cursor));
        }
        return path.reverse();
      }
      for (const direction of directions) {
        const next = { x: current.x + direction.x, y: current.y + direction.y };
        if (previous.has(pointKey(next)) || !this.canRoute(next)) continue;
        previous.set(pointKey(next), current);
        queue.push(next);
      }
    }
    throw new Error("No corridor route between room entrances");
  }

  private canRoute(anchor: Point): boolean {
    return this.footprint(anchor).every(
      (cell) =>
        cell.x >= 2 &&
        cell.y >= 2 &&
        cell.x < 62 &&
        cell.y < 62 &&
        !this.rooms.some(
          (room) =>
            cell.x >= room.x - 1 &&
            cell.x < room.x + room.width + 1 &&
            cell.y >= room.y - 1 &&
            cell.y < room.y + room.height + 1,
        ),
    );
  }

  private footprint(point: Point): Point[] {
    return [
      point,
      { x: point.x + 1, y: point.y },
      { x: point.x, y: point.y + 1 },
      { x: point.x + 1, y: point.y + 1 },
    ];
  }

  private straight(start: Point, end: Point): Point[] {
    const points: Point[] = [];
    const dx = Math.sign(end.x - start.x);
    const dy = Math.sign(end.y - start.y);
    let point = start;
    while (point.x !== end.x || point.y !== end.y) {
      points.push(point);
      point = { x: point.x + dx, y: point.y + dy };
    }
    points.push(end);
    return points;
  }
}
